import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatCurrency, formatDate, getStatusColor } from '../utils/helpers';
import { FileText, Search, Filter, PlusCircle, X, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '../contexts/TenantContext';

const QuotationList = () => {
  const navigate = useNavigate();
  const { buildPath } = useTenant();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});  // Track expanded parent quotations
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    route_type: '',
    date_from: '',
    date_to: '',
    created_from: '',
    created_to: '',
    amount_min: '',
    amount_max: '',
    pax_min: '',
    pax_max: '',
    nights_min: '',
    nights_max: '',
  });

  useEffect(() => {
    fetchQuotations();
  }, [filters.status, filters.route_type]);

  const fetchQuotations = async () => {
    try {
      setLoading(true);
      let url = '/quotations';
      const params = [];
      if (filters.status) params.push(`status=${filters.status}`);
      if (filters.route_type) params.push(`route_type=${filters.route_type}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await api.get(url);
      setQuotations(response.data);
    } catch (error) {
      toast.error('Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  // Filter and sort quotations
  const filteredAndSortedQuotations = useMemo(() => {
    let result = quotations.filter((q) => {
      // Text search
      if (filters.search) {
        const search = filters.search.toLowerCase();
        const matchesSearch = 
          q.quote_reference.toLowerCase().includes(search) ||
          q.guest_name.toLowerCase().includes(search) ||
          q.guest_phone?.includes(search);
        if (!matchesSearch) return false;
      }

      // Tour date range filter
      if (filters.date_from) {
        const tourDate = new Date(q.tour_start_date);
        const fromDate = new Date(filters.date_from);
        if (tourDate < fromDate) return false;
      }
      if (filters.date_to) {
        const tourDate = new Date(q.tour_start_date);
        const toDate = new Date(filters.date_to);
        if (tourDate > toDate) return false;
      }

      // Created date range filter  
      if (filters.created_from) {
        const createdDate = new Date(q.created_at);
        const fromDate = new Date(filters.created_from);
        if (createdDate < fromDate) return false;
      }
      if (filters.created_to) {
        const createdDate = new Date(q.created_at);
        const toDate = new Date(filters.created_to);
        if (createdDate > toDate) return false;
      }

      // Amount range filter
      if (filters.amount_min) {
        if (q.net_quoted_price < parseFloat(filters.amount_min)) return false;
      }
      if (filters.amount_max) {
        if (q.net_quoted_price > parseFloat(filters.amount_max)) return false;
      }

      // Pax range filter
      if (filters.pax_min) {
        if (q.total_pax < parseInt(filters.pax_min)) return false;
      }
      if (filters.pax_max) {
        if (q.total_pax > parseInt(filters.pax_max)) return false;
      }

      // Nights range filter
      if (filters.nights_min) {
        if (q.total_nights < parseInt(filters.nights_min)) return false;
      }
      if (filters.nights_max) {
        if (q.total_nights > parseInt(filters.nights_max)) return false;
      }

      return true;
    });

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle different data types
      if (sortConfig.key === 'created_at' || sortConfig.key === 'tour_start_date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (sortConfig.key === 'net_quoted_price' || sortConfig.key === 'total_pax' || sortConfig.key === 'total_nights') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [quotations, filters, sortConfig]);

  // Group quotations by parent (for clone display)
  const groupedQuotations = useMemo(() => {
    const groups = {};
    const orphans = [];
    
    // First pass: identify parents and clones
    filteredAndSortedQuotations.forEach(q => {
      if (q.parent_quote_id) {
        // This is a clone
        if (!groups[q.parent_quote_id]) {
          groups[q.parent_quote_id] = { parent: null, clones: [] };
        }
        groups[q.parent_quote_id].clones.push(q);
      } else {
        // This is a potential parent
        if (!groups[q.quotation_id]) {
          groups[q.quotation_id] = { parent: q, clones: [] };
        } else {
          groups[q.quotation_id].parent = q;
        }
      }
    });
    
    // Build result array with proper ordering
    const result = [];
    const processedIds = new Set();
    
    filteredAndSortedQuotations.forEach(q => {
      if (processedIds.has(q.quotation_id)) return;
      
      if (q.parent_quote_id) {
        // This is a clone, skip if parent is in the list
        if (groups[q.parent_quote_id]?.parent) return;
        // Orphan clone (parent not in filtered list)
        result.push({ ...q, isClone: true, cloneCount: 0 });
        processedIds.add(q.quotation_id);
      } else {
        // Parent quotation
        const group = groups[q.quotation_id];
        const cloneCount = group?.clones?.length || 0;
        result.push({ ...q, cloneCount, clones: group?.clones || [] });
        processedIds.add(q.quotation_id);
        group?.clones?.forEach(c => processedIds.add(c.quotation_id));
      }
    });
    
    return result;
  }, [filteredAndSortedQuotations]);

  // Toggle expansion for a parent quotation
  const toggleExpand = (quotationId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [quotationId]: !prev[quotationId]
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      route_type: '',
      date_from: '',
      date_to: '',
      created_from: '',
      created_to: '',
      amount_min: '',
      amount_max: '',
      pax_min: '',
      pax_max: '',
      nights_min: '',
      nights_max: '',
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-lg">Loading quotations...</div>
        </div>
      </Layout>
    );
  }

  const SortableHeader = ({ column, label }) => (
    <th 
      className="text-left py-3 px-4 text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-50 select-none"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center">
        {label}
        {getSortIcon(column)}
      </div>
    </th>
  );

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6" data-testid="quotation-list-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">All Quotations</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">{filteredAndSortedQuotations.length} quotations found</p>
          </div>
          <Button
            onClick={() => navigate(buildPath('/quotations/create'))}
            className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
            data-testid="create-quotation-button"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Create Quotation
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            {/* Basic Filters Row */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by quote ref, guest name, or phone..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <div className="w-full sm:w-40">
                <Select
                  value={filters.status || undefined}
                  onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="DRAFT">Draft</SelectItem>
                    <SelectItem value="SENT">Sent</SelectItem>
                    <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                    <SelectItem value="CANCELLED">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-40">
                <Select
                  value={filters.route_type || undefined}
                  onValueChange={(v) => setFilters({ ...filters, route_type: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="route-type-filter">
                    <SelectValue placeholder="All Route Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Route Types</SelectItem>
                    <SelectItem value="predefined">Predefined Route</SelectItem>
                    <SelectItem value="custom">Custom Route</SelectItem>
                    <SelectItem value="custom_package">Custom Package</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="w-full sm:w-auto"
              >
                <Filter className="w-4 h-4 mr-2" />
                More Filters
                <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>

            {/* Advanced Filters */}
            {showAdvancedFilters && (
              <div className="border-t pt-4 space-y-4">
                {/* Date Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Tour Date From</Label>
                    <Input
                      type="date"
                      value={filters.date_from}
                      onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Tour Date To</Label>
                    <Input
                      type="date"
                      value={filters.date_to}
                      onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Created From</Label>
                    <Input
                      type="date"
                      value={filters.created_from}
                      onChange={(e) => setFilters({ ...filters, created_from: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Created To</Label>
                    <Input
                      type="date"
                      value={filters.created_to}
                      onChange={(e) => setFilters({ ...filters, created_to: e.target.value })}
                    />
                  </div>
                </div>

                {/* Range Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Min Amount (₹)</Label>
                    <Input
                      type="number"
                      placeholder="0"
                      value={filters.amount_min}
                      onChange={(e) => setFilters({ ...filters, amount_min: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Max Amount (₹)</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={filters.amount_max}
                      onChange={(e) => setFilters({ ...filters, amount_max: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Min Pax</Label>
                    <Input
                      type="number"
                      placeholder="1"
                      value={filters.pax_min}
                      onChange={(e) => setFilters({ ...filters, pax_min: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Max Pax</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={filters.pax_max}
                      onChange={(e) => setFilters({ ...filters, pax_max: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Min Nights</Label>
                    <Input
                      type="number"
                      placeholder="1"
                      value={filters.nights_min}
                      onChange={(e) => setFilters({ ...filters, nights_min: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-gray-600">Max Nights</Label>
                    <Input
                      type="number"
                      placeholder="No limit"
                      value={filters.nights_max}
                      onChange={(e) => setFilters({ ...filters, nights_max: e.target.value })}
                    />
                  </div>
                </div>

                {/* Clear Filters */}
                {hasActiveFilters && (
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-2" />
                      Clear All Filters
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quotations Table */}
        <Card>
          <CardContent className="pt-6">
            {filteredAndSortedQuotations.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No quotations found</p>
                <Button onClick={() => navigate(buildPath('/quotations/create'))}>Create Your First Quote</Button>
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="block lg:hidden space-y-4">
                  {filteredAndSortedQuotations.map((quote) => (
                    <div
                      key={quote.quotation_id}
                      onClick={() => navigate(buildPath(`/quotations/${quote.quotation_id}`))}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      data-testid={`quote-card-${quote.quote_reference}`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-semibold text-blue-600 text-base">{quote.quote_reference}{quote.version_label && <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded" data-testid={`quote-version-badge-${quote.quote_reference}`}>{quote.version_label}</span>}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Created: {formatDate(quote.created_at)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                              quote.status
                            )}`}
                          >
                            {quote.status}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            quote.route_type === 'predefined' ? 'bg-blue-100 text-blue-700' :
                            quote.route_type === 'custom' ? 'bg-purple-100 text-purple-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {quote.route_type === 'predefined' ? 'Predefined' : 
                             quote.route_type === 'custom' ? 'Custom' : 'Package'}
                          </span>
                          {quote.source === 'ai_assistant' && (
                            <span data-testid={`ai-badge-mobile-${quote.quotation_id}`} className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 inline-flex items-center gap-0.5">
                              <Sparkles className="h-3 w-3" /> AI
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Guest:</span>
                          <span className="font-medium text-gray-900">{quote.guest_name}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tour Date:</span>
                          <span className="font-medium text-gray-900">{formatDate(quote.tour_start_date)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Duration:</span>
                          <span className="font-medium text-gray-900">{quote.total_days}D/{quote.total_nights}N</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Pax:</span>
                          <span className="font-medium text-gray-900">{quote.total_pax}</span>
                        </div>
                        <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                          <span className="text-gray-600">Amount:</span>
                          <span className="font-semibold text-gray-900 text-base">
                            {formatCurrency(quote.net_quoted_price)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View with Sorting */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full" data-testid="quotations-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <SortableHeader column="quote_reference" label="Quote Ref" />
                      <SortableHeader column="guest_name" label="Guest Name" />
                      <SortableHeader column="created_at" label="Created" />
                      <SortableHeader column="tour_start_date" label="Tour Date" />
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Duration</th>
                      <SortableHeader column="total_pax" label="Pax" />
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Route Type</th>
                      <SortableHeader column="net_quoted_price" label="Amount" />
                      <SortableHeader column="status" label="Status" />
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedQuotations.map((quote) => (
                      <React.Fragment key={quote.quotation_id}>
                        {/* Parent/Main Row */}
                        <tr
                          className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${quote.cloneCount > 0 ? 'bg-white' : ''}`}
                          onClick={() => navigate(buildPath(`/quotations/${quote.quotation_id}`))}
                          data-testid={`quote-row-${quote.quote_reference}`}
                        >
                          <td className="py-3 px-4 text-sm font-medium text-blue-600">
                            <div className="flex items-center gap-2">
                              {quote.cloneCount > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(quote.quotation_id);
                                  }}
                                  className="p-0.5 hover:bg-gray-200 rounded"
                                  data-testid={`expand-quote-${quote.quote_reference}`}
                                >
                                  {expandedGroups[quote.quotation_id] ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                  )}
                                </button>
                              )}
                              {quote.isClone && <span className="text-gray-400 pl-4">└─</span>}
                              <span>{quote.quote_reference}</span>
                              {quote.version_label && <span className="ml-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-semibold rounded" data-testid={`quote-version-badge-${quote.quote_reference}`}>{quote.version_label}</span>}
                              {quote.cloneCount > 0 && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                  {quote.cloneCount}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900">{quote.guest_name}</td>
                          <td className="py-3 px-4 text-sm text-gray-500">
                            {formatDate(quote.created_at)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {formatDate(quote.tour_start_date)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">
                            {quote.total_days}D/{quote.total_nights}N
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{quote.total_pax}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              quote.route_type === 'predefined' ? 'bg-blue-100 text-blue-700' :
                              quote.route_type === 'custom' ? 'bg-purple-100 text-purple-700' :
                              'bg-orange-100 text-orange-700'
                            }`}>
                              {quote.route_type === 'predefined' ? 'Predefined' : 
                               quote.route_type === 'custom' ? 'Custom' : 'Package'}
                            </span>
                            {quote.source === 'ai_assistant' && (
                              <span data-testid={`ai-badge-desktop-${quote.quotation_id}`} className="ml-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 inline-flex items-center gap-0.5">
                                <Sparkles className="h-3 w-3" /> AI
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-sm font-semibold text-gray-900">
                            {formatCurrency(quote.net_quoted_price)}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                quote.status
                              )}`}
                            >
                              {quote.status}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(buildPath(`/quotations/${quote.quotation_id}`));
                              }}
                              data-testid={`view-quote-${quote.quote_reference}`}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                        
                        {/* Clone Rows (expanded) */}
                        {expandedGroups[quote.quotation_id] && quote.clones?.map((clone) => (
                          <tr
                            key={clone.quotation_id}
                            className="border-b border-gray-100 bg-slate-50 hover:bg-slate-100 cursor-pointer"
                            onClick={() => navigate(buildPath(`/quotations/${clone.quotation_id}`))}
                            data-testid={`quote-row-${clone.quote_reference}`}
                          >
                            <td className="py-2 px-4 text-sm font-medium text-blue-600">
                              <div className="flex items-center gap-2 pl-6">
                                <span className="text-gray-400">└─</span>
                                <span>{clone.quote_reference}</span>
                              </div>
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-600">{clone.guest_name}</td>
                            <td className="py-2 px-4 text-sm text-gray-500">
                              {formatDate(clone.created_at)}
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-600">
                              {formatDate(clone.tour_start_date)}
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-600">
                              {clone.total_days}D/{clone.total_nights}N
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-600">{clone.total_pax}</td>
                            <td className="py-2 px-4">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                clone.route_type === 'predefined' ? 'bg-blue-100 text-blue-700' :
                                clone.route_type === 'custom' ? 'bg-purple-100 text-purple-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {clone.route_type === 'predefined' ? 'Predefined' : 
                                 clone.route_type === 'custom' ? 'Custom' : 'Package'}
                              </span>
                            </td>
                            <td className="py-2 px-4 text-sm font-semibold text-gray-900">
                              {formatCurrency(clone.net_quoted_price)}
                            </td>
                            <td className="py-2 px-4">
                              <span
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                  clone.status
                                )}`}
                              >
                                {clone.status}
                              </span>
                            </td>
                            <td className="py-2 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(buildPath(`/quotations/${clone.quotation_id}`));
                                }}
                                data-testid={`view-quote-${clone.quote_reference}`}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default QuotationList;
